import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { Metrics } from 'components/insights/constants';
import { formatDaysValue, formatDecimal, formatPercentage, formatWholeNumber } from 'components/insights/formatting';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';

// --- Row type ---

export type EntityBreakdownRow = {
  entityName: string;
  values: Record<string, number | null>;
};

// --- Column definitions ---

export const ENTITY_DEFAULT_SORT = { id: 'totalDischarges', desc: true };

const CELL_PADDING = { meta: { padding: '16px 12px' } };
const CELL_PADDING_LEFT = { meta: { padding: '16px 12px 16px 24px' } };

export function buildEntityBreakdownColumns(entityHeader: string): ColumnDef<EntityBreakdownRow, any>[] {
  const helper = createColumnHelper<EntityBreakdownRow>();

  return [
    helper.accessor('entityName', {
      header: entityHeader,
      cell: (info) => info.getValue(),
      size: 210,
      enableSorting: true,
      ...CELL_PADDING_LEFT,
    }),
    helper.accessor((row) => row.values.totalDischarges ?? null, {
      id: 'totalDischarges',
      header: 'Total Discharges',
      cell: (info) => formatWholeNumber(info.getValue()),
      size: 150,
      enableSorting: true,
      ...CELL_PADDING_LEFT,
    }),
    helper.accessor((row) => row.values.avgLos ?? null, {
      id: 'avgLos',
      header: 'Avg. Length of Service',
      cell: (info) => formatDaysValue(info.getValue()),
      size: 170,
      enableSorting: true,
      ...CELL_PADDING,
    }),
    helper.accessor((row) => row.values.readmissionRate ?? null, {
      id: 'readmissionRate',
      header: 'Readmission Rate',
      cell: (info) => formatPercentage(info.getValue()),
      size: 150,
      enableSorting: true,
      ...CELL_PADDING,
    }),
    helper.accessor((row) => row.values.oes ?? null, {
      id: 'oes',
      header: 'Olio Engagement Score (OES)',
      cell: (info) => formatPercentage(info.getValue(), 0),
      size: 210,
      enableSorting: true,
      ...CELL_PADDING,
    }),
    helper.accessor((row) => row.values.engagementsPerPatient ?? null, {
      id: 'engagementsPerPatient',
      header: 'Engagements Per Patient Per Week',
      cell: (info) => formatDecimal(info.getValue()),
      size: 240,
      enableSorting: true,
      ...CELL_PADDING,
    }),
  ];
}

// --- Row builder ---

const DISCHARGE_METRICS = [
  { key: 'totalDischarges', metric: Metrics.ID_COUNT },
  { key: 'avgLos', metric: Metrics.LENGTH_OF_STAY_AVERAGE },
  { key: 'readmissionRate', metric: Metrics.READMISSION_RATE },
  { key: 'oes', metric: Metrics.OLIO_ENGAGEMENT_SCORE },
  { key: 'engagementsPerPatient', metric: Metrics.ACTIVITIES_PER_PATIENT_PER_WEEK },
];

export function buildEntityBreakdownRows(dischargeRollups: AnalyticsResponseRollupCollection): EntityBreakdownRow[] {
  const { analyticsResponse } = dischargeRollups;

  return analyticsResponse.responseData
    .filter((row) => (row.grouping ?? 0) === 0 && (row.datesIndex ?? 0) === 0)
    .map((row) => {
      const entityName = String(row.dimensionValues[0]?.value ?? '');
      const values: Record<string, number | null> = {};
      for (const { key, metric } of DISCHARGE_METRICS) {
        const idx = analyticsResponse.findMetricIndex(metric);
        values[key] =
          idx >= 0 ? (row.metricValues[idx]?.value != null ? Number(row.metricValues[idx]!.value) : null) : null;
      }
      return { entityName, values };
    })
    .filter((row) => row.entityName);
}
